import { TrashIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { sourcesList } from '../../../../../constants';
import { accountSelector } from '../../../../../store';
import { setProjectSources } from '../../../../../store/Projects/Project/ProjectBudget';
import { formatNumber, projectBudgetTotal } from '../../../../../utils';
import Button from '../../../../UI/Button';
import { HookForm } from '../../../../UI/HookForm/Form';
import HookMaskedInput from '../../../../UI/HookForm/Form/MaskedInput';
import { CurrencyFormatDisplay } from '../../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import styles from '../Form.module.css';

let initialRender = true;

export const ProjectSourcesForm = ({
  ProjectSourcesState,
  handleProgress,
}: {
  ProjectSourcesState: any;
  handleProgress: (type: string) => void;
}) => {
  const dispatch = useDispatch();
  const { data: currentClientAccount } = useSelector(accountSelector);
  const { trigger, getValues, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: 'sources',
  });
  const projectSourcesFormValues = useWatch({ name: 'sources' });
  const budgetTotal = formatNumber(projectBudgetTotal(projectSourcesFormValues));

  useEffect(() => {
    if (currentClientAccount.subdomain === 'chicagorebuild' && initialRender) {
      initialRender = false;
      append({ name: 'CCLF Loan', amount: 0 });
      dispatch(setProjectSources({ name: 'CCLF Loan', amount: 0 }));
    }
  }, [append, currentClientAccount.subdomain, dispatch]);

  const addNewSource = async () => {
    const projectSourcesValidation = await trigger('sources');
    if (projectSourcesValidation || getValues('sources').length === 0) {
      append({ name: '', amount: 0 });
    }
  };

  const backHandler = () => {
    const transformedData = getValues('sources');
    handleProgress('BACK');
    dispatch(setProjectSources(transformedData));
  };

  const onSubmit = (data: any) => {
    const transformedData = {
      ...data,
    };
    handleProgress('FORWARD');
    dispatch(setProjectSources(transformedData.sources));
  };

  return (
    <HookForm onSubmit={onSubmit}>
      <div className='relative'>
        <h2 className={`mb-0 ${styles['form-title']}`}>Project Sources</h2>
        <p className='text-sm text-neutralColor-normal'>Optional</p>
        {fields.map((sources, idx) => {
          return (
            <div key={sources.id} className='flex items-center justify-between gap-4 my-4'>
              <div className='grid grid-cols-2 gap-5 w-full'>
                <HookSelect
                  customClassName='col-span-1'
                  label='Line Item'
                  name={`sources.${idx}.name` as const}
                  id='name'
                  options={sourcesList.map((d) => ({
                    label: d,
                    value: d,
                  }))}
                />
                <HookMaskedInput
                  customClassName='col-span-1'
                  label='Amount ($)'
                  name={`sources.${idx}.amount` as const}
                  id='amount'
                  maskedComponent='currency'
                />
              </div>
              <div
                className='h-full border p-2 mt-6 rounded-md text-neutralColor-normal bg-gray-50 hover:bg-tertiaryColor-lightest hover:text-tertiaryColor-normal cursor-pointer transition duration-100 ease-in-out'
                onClick={() => remove(idx)}
              >
                <TrashIcon className='h-5 w-6' />
              </div>
            </div>
          );
        })}
        <p className='mt-5 font-bold text-primaryColor-normal'>
          Total: <CurrencyFormatDisplay value={budgetTotal} prefix='$ ' />
        </p>
        <div className='mt-8'>
          <Button label='Add Source' btnStyle='btn-primary' btnSize='btn-sm' type='button' onClick={addNewSource} />
        </div>
      </div>
      <div className='w-full flex justify-end gap-4'>
        <Button type='button' btnStyle='btn-neutral' label='Previous' onClick={backHandler} />
        <Button type='submit' btnStyle='btn-primary' label='Next' />
      </div>
    </HookForm>
  );
};
